import { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const LoginContext = createContext();

export const LoginProvider = ({ children }) => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);

  // Memoize fetchPermissions to prevent unnecessary recreations
  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/all-permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch permissions");
      }
      
      const data = await response.json();
      setPermissions(data.modules);
      return data.modules; // Return the permissions
    } catch (err) {
      console.error("Error fetching permissions:", err);
      toast.error("Error loading permissions");
      return null;
    }
  }, [API_BASE_URL]);

  // Initialize user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
        fetchPermissions();
      } catch (error) {
        console.error("Error parsing stored user:", error);
        toast.error("Error loading user data");
      }
    }
  }, [fetchPermissions]);

  // Redirect if not authenticated
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isAuthenticated = user || localStorage.getItem("user");
    const publicRoutes = ["/", "/forgot-password", "/reset-password", "/public-form", "/sign-in"];
    const authRoutes = ["/", "/sign-in"]; // Routes that should redirect if logged in

    if (isAuthenticated && authRoutes.includes(currentPath)) {
      navigate("/dashboard"); // Redirect logged-in users away from auth pages
      return;
    }

    if (!isAuthenticated && !publicRoutes.includes(currentPath)) {
      navigate("/sign-in"); // Redirect to sign-in instead of root for clarity
    }
  }, [user, navigate]);

  const login = async (phoneNumber, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Invalid credentials");
      }

      const data = await response.json();
      const token = data.token;

      // Update state and storage
      setUser(data.user);
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(data.user));

      // Fetch permissions after successful login
      const fetchedPermissions = await fetchPermissions();
      
      if (fetchedPermissions) {
        toast.success("Login successful!");
        navigate("/dashboard");
      } else {
        throw new Error("Error Fetching Permissions");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
  

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setPermissions(null);
    navigate("/");
  };

  const hasPermission = (module, action) => {
    if (!user || !permissions) return false;

    const modulePermissions = permissions[module];
    if (!modulePermissions) return false;

    const allowedRoles = modulePermissions[action];
    if (!allowedRoles) return false;

    return allowedRoles.includes(user.userType);
  };

  return (
    <LoginContext.Provider value={{ 
      user, 
      loading, 
      error, 
      login, 
      logout,
      permissions,
      hasPermission
    }}>
      {children}
    </LoginContext.Provider>
  );
};